import React, { useState, useEffect, useRef } from 'react';
import { Box, Chip, CircularProgress, IconButton, Fade } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Refresh } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'dirty';

export interface AutosaveIndicatorProps {
  status: AutosaveStatus;
  lastSaveTime?: Date;
  onRetry?: () => void;
}

export const AutosaveIndicator: React.FC<AutosaveIndicatorProps> = ({
  status,
  lastSaveTime,
  onRetry
}) => {
  const theme = useTheme();
  const [showSaved, setShowSaved] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const isMountedRef = useRef(true);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-hide "saved" status after 3 seconds
  useEffect(() => {
    if (status === 'saved') {
      // Use queueMicrotask to avoid cascading render warnings
      queueMicrotask(() => {
        if (isMountedRef.current) {
          setShowSaved(true);
          setFadeOut(false);
        }
      });

      const fadeTimer = setTimeout(() => {
        if (isMountedRef.current) {
          setFadeOut(true);
        }
      }, 2500);

      const hideTimer = setTimeout(() => {
        if (isMountedRef.current) {
          setShowSaved(false);
        }
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [status]);

  // Don't show anything when idle or dirty (unsaved edit — clears "saved" chip immediately)
  if (status === 'idle' || status === 'dirty' || (status === 'saved' && !showSaved)) {
    return null;
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'saving':
        return {
          icon: <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />,
          label: 'Saving...',
          color: 'default' as const,
          sx: { backgroundColor: theme.palette.action.hover }
        };
      case 'saved':
        return {
          icon: <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />,
          label: 'All changes saved',
          color: 'success' as const,
          sx: {}
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />,
          label: 'Error saving',
          color: 'error' as const,
          sx: {}
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <Fade in={!fadeOut} timeout={500}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={config.icon}
          label={config.label}
          color={config.color}
          size="small"
          sx={{
            height: 28,
            fontSize: '0.75rem',
            fontWeight: 500,
            ...config.sx
          }}
        />

        {status === 'error' && onRetry && (
          <IconButton
            size="small"
            onClick={onRetry}
            sx={{
              width: 28,
              height: 28,
              color: theme.palette.error.main
            }}
            aria-label="Retry save"
          >
            <Refresh sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {status === 'saved' && lastSaveTime && (
          <Box
            component="span"
            sx={{
              fontSize: '0.7rem',
              color: theme.palette.text.secondary,
              display: { xs: 'none', sm: 'inline' }
            }}
          >
            {formatSaveTime(lastSaveTime)}
          </Box>
        )}
      </Box>
    </Fade>
  );
};

function formatSaveTime(date: Date): string {
  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSeconds < 10) {
    return 'just now';
  } else if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes}m ago`;
  } else {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours}h ago`;
  }
}
